/**
 * Rota adımı kuralları: hangi adım hangi kanıtı ister, fail'de nereye dönülür (03- §19.1).
 * Saf fonksiyonlar; API ve web aynı kuralı okur.
 */
/**
 * Adım adları veritabanında duruyor ve iki dilli olabilir: eski satırlar Türkçe seed'den geldi,
 * yeni satırlar İngilizce. Regex ikisini de tanımalı; yalnız birini tanırsa kural sessizce
 * kaybolur (typecheck de test de görmez).
 */
export const testAdimiMi = (ad) => /NDT|DPI|MPI|Hidrotest|Hydrotest/.test(ad);
/**
 * Hidrotest adımı rapor kanıtını ARTIK ROTADA istemez (03.09.2026 kullanıcı kararı): HT raporu
 * Finishing ekranında hat bazında yükleniyor. Rotada beklemek tamir zincirini kilitliyordu ve
 * riser assembly'e hiç geçemiyordu. Adım yine bir test adımıdır (fail → geri dönüş çalışır),
 * yalnız kapanışı PDF'e bağlı değildir.
 */
export const hidrotestAdimiMi = (ad) => /Hidrotest|Hydrotest/.test(ad);
export const pwhtAdimiMi = (ad) => /^PWHT/.test(ad);
export const kaynakAdimiMi = (ad) => /^(Kaynak|Welding)/.test(ad);
export const fotoAdimiMi = (ad) => /Washing \(final\)/.test(ad) || /^Dress-out/.test(ad);
export const FITUP_HAZIRLIK = "Fit-up preparation";
export const FITUP_ONAY = "Fit-up approval (G1)";
/**
 * D-031 "No record available": kalite kapısı olan adımlar (G1 onayı, kaynak, NDT/test, hidrotest, PWHT)
 * yalnız kalite lideri ve PM tarafından atlanabilir; ötekileri formen ve saha süpervizörü de atlayabilir.
 * D-043: gerekçe isteğe bağlı (kalite kapısı adımları dahil); en az uzunluk kuralı yok.
 */
export const kayitYokKapiMi = (ad) => ad === FITUP_ONAY || kaynakAdimiMi(ad) || testAdimiMi(ad) || pwhtAdimiMi(ad);
/**
 * D-037: kaynak başına fit-up / paso sayacı; API bunu çağırır, adimEngeli bunu okur.
 * mod: kaynak yok → "0"; plan var → "P" (her kaynağın formu weldId ile); plan yok, kaynak var → "L" (weldId boş tek form
 * bütün kaynakları karşılar).
 */
export function kaynakSayaci(g) {
    const mod = g.weldler.length === 0 ? "0" : g.plan ? "P" : "L";
    const ortak = g.fituplar.find((f) => f.weldId === null) ?? null;
    const kaynaklar = [...g.weldler]
        .sort((a, b) => a.no - b.no)
        .map((w) => {
        const f = mod === "L" ? ortak : g.fituplar.find((x) => x.weldId === w.id) ?? null;
        return { weldId: w.id, no: w.no, fitupId: f?.id ?? null, hazir: Boolean(f?.hazirTs), release: Boolean(f?.releaseTs), paso: w.pasoSayisi };
    });
    return {
        mod,
        planVar: g.plan !== null,
        kesimYok: g.plan?.kesimYok ?? false,
        toplam: kaynaklar.length,
        hazir: kaynaklar.filter((k) => k.hazir).length,
        release: kaynaklar.filter((k) => k.release).length,
        pasolu: kaynaklar.filter((k) => k.paso > 0).length,
        paso: kaynaklar.reduce((n, k) => n + k.paso, 0),
        kaynaklar,
    };
}
/** D-037: kaynak yokken fit-up hazırlığı, G1 ve kaynak adımlarının engeli. */
export const KESIM_PLANI_YOK_ENGELI = "Cut plan not approved: no welds. Fit-up preparation, Fit-up approval (G1) and Welding do not close until a cut plan is approved for this item.";
/** D-035: plan "No cut" ile onaylandıysa (0 kaynak) aynı üç adım yalnız "No record available" ile kapanır. */
export const KESIMSIZ_PLAN_ENGELI = "The approved cut plan has no cut (No cut): no welds. Fit-up preparation, Fit-up approval (G1) and Welding close only with “No record available”.";
/** "W3" · "W3 and W4" · "W3, W4 and W5" */
const kaynakListesi = (nolar) => {
    const w = nolar.map((n) => `W${n}`);
    return w.length <= 1 ? (w[0] ?? "") : `${w.slice(0, -1).join(", ")} and ${w[w.length - 1]}`;
};
/** Adımın kapanmasını engelleyen şart; boş string = kapatılabilir. Duvar UI'da değil işlemde. */
export function adimEngeli(b) {
    const k = b.kaynak;
    if (b.ad === FITUP_HAZIRLIK || b.ad === FITUP_ONAY || kaynakAdimiMi(b.ad)) {
        if (k.mod === "0")
            return k.kesimYok ? KESIMSIZ_PLAN_ENGELI : KESIM_PLANI_YOK_ENGELI;
        if (k.mod === "L") {
            // Eski iş: kalem düzeyi form bütün kaynakları karşılar; bugünkü üç metin.
            const f = k.kaynaklar[0];
            if (b.ad === FITUP_HAZIRLIK && !f?.hazir)
                return "This step does not close until the foreman says “We are ready” on the fit-up screen (quality control call).";
            if (b.ad === FITUP_ONAY && !f?.release)
                return "This step does not close until the fit-up form is filled in by the quality controller and release is given.";
            if (kaynakAdimiMi(b.ad) && k.paso === 0 && !b.weldMapPdf)
                return "The welding step does not close until at least one pass is entered on the weld map, or the completed weld map PDF is uploaded.";
        }
        else {
            if (b.ad === FITUP_HAZIRLIK && k.hazir < k.toplam)
                return `This step does not close until the foreman says “We are ready” on the fit-up form of every weld: ${k.hazir}/${k.toplam} welds ready.`;
            if (b.ad === FITUP_ONAY && k.release < k.toplam) {
                const bekleyen = k.kaynaklar.filter((x) => !x.release).map((x) => x.no);
                return `This step does not close until the quality controller fills in the fit-up form of every weld and gives release: ${k.release}/${k.toplam} welds released (${kaynakListesi(bekleyen)} awaiting G1).`;
            }
            if (kaynakAdimiMi(b.ad) && k.pasolu < k.toplam && !b.weldMapPdf)
                return `The welding step does not close until every weld has at least one pass on the weld map (${k.pasolu}/${k.toplam} welds with passes), or the completed weld map PDF is uploaded.`;
        }
    }
    if (testAdimiMi(b.ad) && !hidrotestAdimiMi(b.ad) && !b.raporVar)
        return "The test step does not close until the report PDF is uploaded and approved. (Upload + Approve/Fail: Quality Eng.)";
    if (pwhtAdimiMi(b.ad) && !b.raporVar)
        return "The step does not close until the PWHT external service report (PDF) is uploaded — single step, report only.";
    if (fotoAdimiMi(b.ad) && b.fotoSayisi === 0)
        return b.ad.startsWith("Dress") ? "Dress-out does not close until the foreman uploads a photo." : "The pre-shipment step does not close until general photos of the product are added.";
    return "";
}
/**
 * Fail kuralı: bir önceki rota test adımından SONRAKİ adıma dönülür; önceki test yoksa en başa.
 * Manuel loglar aktif adım olamaz. Döner: hedef sıra ve dönülen test adımının indeksi (-1 = başa).
 */
export function failHedefi(adimlar, aktif) {
    let j = -1;
    for (let k = aktif - 1; k >= 0; k--) {
        if (adimlar[k].tip === "rota" && testAdimiMi(adimlar[k].ad)) {
            j = k;
            break;
        }
    }
    let hedef = j >= 0 ? j + 1 : 0;
    while (hedef < adimlar.length && adimlar[hedef].tip === "manuel")
        hedef++;
    return { hedef, oncekiTest: j };
}
/** WPS süreç akışı metni (prototip wpsSurecMetni). */
export function wpsSurecMetni(w) {
    if (!w)
        return "";
    return (w.minTig > 1 ? `first ≥${w.minTig} paso TIG` : "TIG") + (w.proc.length > 1 ? " → then " + w.proc.slice(1).join(" → ") : " (single process)");
}
//# sourceMappingURL=rota.js.map